import { MuiMenuSelect, type MuiMenuSelectOption } from "@/components/ui/mui-menu-select";
import { MuiSelectCaret } from "@/components/ui/mui-select-caret";
import { OutlinedField } from "@/components/ui/outlined-field";
import {
  SPEC_ITEM_STATUS_DOT,
  SPEC_ITEM_STATUSES,
  type SpecItemStatus,
} from "@/components/ui/spec-item-status";
import { cn } from "@/lib/utils";

// The status pill — the donor's shared `StatusItemSelect`
// (`src/components/specSheets/StatusItemSelect.tsx`), which the Editor's line
// rows draw in their first cell (#809). Shared because the donor's is: the spec
// sheets' shared folder.
//
// Value for value, measured on the running donor at 1200×858:
//
//   - **the control** — an outlined MUI `Select` in a `FormControl` 46×32,
//     radius 55 (`:39-60`), its outline the shared field's 23% line;
//     `disabled` while its write runs; the caret at `right: 0` (the `sx`'s
//     `.MuiSvgIcon-root`), 4px from the top;
//   - **the rendered value** — a 16px round dot in the status colour at
//     `margin: 3px 0 0 -5px`, 0.5 opacity while loading (`:61-73`): MUI's
//     outlined display (16.5px / 14px padding round a 23px line, 32px kept for
//     the caret) centred in the 32px box, so the dot lands 9px in and 7.5px
//     down;
//   - **the menu** — opening in place (`MenuProps.disablePortal`), `Finalized`,
//     `In Progress`, `Needs Attention` in `LineStatusText` order, each a 16px
//     dot 8px before its label (`:20-36,77-79`), in `SPEC_ITEM_STATUS_COLORS`
//     (`styles/shared.ts:1-5`);
//   - **the click stops here** — the `FormControl`'s `onClick` is the row's
//     `stopPropagation` (`LineStatus.tsx:30`), so neither opening the menu nor
//     a pick in it sets the row's Active selection by itself.
//
// **Named, where the donor's is not** — `Line status`, its value read from a
// hidden label beside the dot. The donor's combobox has no name and only a
// colour for a value: an a11y-tree-only break with no pixel moved, kept for
// the decision menu.

/** `LineStatusMapping` (`common.ts:225-235`). */
const LINE_STATUS_LABEL: Record<SpecItemStatus, string> = {
  finalized: "Finalized",
  in_progress: "In Progress",
  needs_attention: "Needs Attention",
};

const OPTIONS: MuiMenuSelectOption[] = SPEC_ITEM_STATUSES.map((status) => ({
  value: status,
  label: LINE_STATUS_LABEL[status],
  content: (
    <span className="flex items-center gap-2">
      <span
        data-status-dot
        className={cn("size-4 shrink-0 rounded-full", SPEC_ITEM_STATUS_DOT[status])}
      />
      <span>{LINE_STATUS_LABEL[status]}</span>
    </span>
  ),
}));

export interface StatusItemSelectProps {
  status: SpecItemStatus;
  /** The write is running: disabled, the dot at half opacity. */
  isLoading: boolean;
  /** A pick of another status; the current one reports nothing (`Select`'s `onChange`). */
  onStatusChange: (status: SpecItemStatus) => void;
}

export function StatusItemSelect({ status, isLoading, onStatusChange }: StatusItemSelectProps) {
  return (
    <div
      data-testid="line-status-select"
      // The `FormControl`'s `handleIconClick` — every click in the pill, its
      // in-place menu included, stops here.
      onClick={(event) => event.stopPropagation()}
      className="w-[46px]"
    >
      <OutlinedField
        label=""
        shrink={false}
        size="small"
        disabled={isLoading}
        frameClassName="h-8 min-h-8 rounded-[55px]"
      >
        <MuiMenuSelect
          aria-label="Line status"
          value={status}
          options={OPTIONS}
          onValueChange={(value) => onStatusChange(value as SpecItemStatus)}
          disabled={isLoading}
          disablePortal
          scrollLock
          renderValue={(option) => (
            <span
              data-testid="line-status-select-value"
              className={cn(
                "mt-[3px] -ml-[5px] block size-4 rounded-full",
                SPEC_ITEM_STATUS_DOT[option.value as SpecItemStatus],
                isLoading && "opacity-50",
              )}
            >
              <span className="sr-only">{option.label}</span>
            </span>
          )}
          anchorClassName="flex h-8 w-full items-center"
          className={cn(
            // MUI's outlined display: 16.5px / 14px round a 23px line, 32px
            // kept for the caret — taller than the pill and centred in it.
            "block h-14 w-full min-w-0 shrink-0 cursor-pointer overflow-hidden py-[16.5px] pr-8 pl-3.5 text-base leading-[23px] outline-none select-none",
            "aria-disabled:cursor-default",
          )}
          caret={(open) => (
            <MuiSelectCaret
              open={open}
              className={cn("right-0", isLoading && "text-lennar-action-disabled opacity-100")}
            />
          )}
        />
      </OutlinedField>
    </div>
  );
}
